/**
 * Hardware-enclave-bound keypair abstraction.
 *
 * The player binary must NEVER hold raw private key bytes — that is the
 * whole reason this module exists. The private key is generated inside
 * the platform's secure enclave (Android Keystore, iOS Secure Enclave,
 * or — in the browser dev fallback — Web Crypto with `extractable: false`)
 * and exposes only a sign() method.
 *
 * Web Crypto's `extractable: false` is "soft" hardware binding (the key
 * lives inside the WebView's IndexedDB, the algorithm refuses to export
 * it). On native we MUST swap to a real Keystore plugin — see the
 * `nativeImpl` section below for the contract.
 *
 * The wire protocol this implements is documented in SECURITY.md.
 */

const KEY_NAME = 'sensar-device-signing-key';
const ALG: EcKeyImportParams & EcKeyGenParams = {
  name: 'ECDSA',
  namedCurve: 'P-256',
};

/** Public face of any backing implementation. */
export interface KeyHandle {
  /** Signature algorithm constant. Must match server's KEY_ALG. */
  readonly alg: 'ECDSA_P256_SHA256';
  /** SubjectPublicKeyInfo, DER-encoded, base64. Sent to server on register. */
  readonly publicKeyDer: string;
  /** lowercase hex sha256(publicKeyDer). The board's stable wire identity. */
  readonly fingerprint: string;
  /** Sign arbitrary bytes; returns DER signature, base64. */
  sign(payload: Uint8Array): Promise<string>;
}

// ─── Web Crypto backend (browser dev) ──────────────────────────────────
//
// Persistent across reloads thanks to IndexedDB. NOT hardware-backed in
// any meaningful sense — fine for local dev, must be replaced before
// shipping to physical hardware.

const IDB_DB = 'sensar-keystore';
const IDB_STORE = 'keys';

async function idbGet(): Promise<CryptoKeyPair | null> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const tx = req.result.transaction(IDB_STORE, 'readonly');
      const get = tx.objectStore(IDB_STORE).get(KEY_NAME);
      get.onsuccess = () => resolve((get.result as CryptoKeyPair | undefined) ?? null);
      get.onerror = () => reject(get.error);
    };
  });
}

async function idbPut(pair: CryptoKeyPair): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const tx = req.result.transaction(IDB_STORE, 'readwrite');
      const put = tx.objectStore(IDB_STORE).put(pair, KEY_NAME);
      put.onsuccess = () => resolve();
      put.onerror = () => reject(put.error);
    };
  });
}

function bufToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function bufToHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}

async function webBackend(): Promise<KeyHandle> {
  let pair = await idbGet();
  if (!pair) {
    pair = (await crypto.subtle.generateKey(
      ALG,
      false, // private key NOT extractable
      ['sign', 'verify'],
    )) as CryptoKeyPair;
    await idbPut(pair);
  }

  const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
  const publicKeyDer = bufToBase64(spki);
  const fingerprint = bufToHex(await crypto.subtle.digest('SHA-256', spki));
  const privateKey = pair.privateKey;

  return {
    alg: 'ECDSA_P256_SHA256',
    publicKeyDer,
    fingerprint,
    async sign(payload: Uint8Array): Promise<string> {
      // Web Crypto returns raw r||s — server expects DER. Convert.
      // Copy into a fresh ArrayBuffer-backed Uint8Array so the lib
      // typings (BufferSource over ArrayBuffer, not SharedArrayBuffer)
      // are satisfied without relaxing tsconfig.
      const buf = new Uint8Array(payload.byteLength);
      buf.set(payload);
      const raw = await crypto.subtle.sign(
        { name: 'ECDSA', hash: 'SHA-256' },
        privateKey,
        buf,
      );
      return bufToBase64(rawSignatureToDer(new Uint8Array(raw)));
    },
  };
}

/** Convert Web Crypto's raw r||s ECDSA signature (64 bytes) to DER. */
function rawSignatureToDer(raw: Uint8Array): ArrayBuffer {
  if (raw.length !== 64) throw new Error('expected 64-byte raw ECDSA signature');
  const r = trimLeadingZeros(raw.slice(0, 32));
  const s = trimLeadingZeros(raw.slice(32, 64));
  const rEnc = encodeInteger(r);
  const sEnc = encodeInteger(s);
  const seq = new Uint8Array(2 + rEnc.length + sEnc.length);
  seq[0] = 0x30; // SEQUENCE
  seq[1] = rEnc.length + sEnc.length;
  seq.set(rEnc, 2);
  seq.set(sEnc, 2 + rEnc.length);
  return seq.buffer;
}
function trimLeadingZeros(b: Uint8Array): Uint8Array {
  let i = 0;
  while (i < b.length - 1 && b[i] === 0) i++;
  return b.slice(i);
}
function encodeInteger(b: Uint8Array): Uint8Array {
  // Prepend 0x00 if high bit set (DER ASN.1 INTEGER must be non-negative).
  const needsPad = (b[0] & 0x80) !== 0;
  const body = needsPad ? new Uint8Array([0, ...b]) : b;
  const out = new Uint8Array(2 + body.length);
  out[0] = 0x02; // INTEGER
  out[1] = body.length;
  out.set(body, 2);
  return out;
}

// ─── Native backend (TODO: Capacitor plugin) ───────────────────────────
//
// On Android: bind to AndroidKeyStore via a custom plugin. Spec for the
// plugin author:
//
//   plugin: sensar-keystore
//   methods:
//     getOrCreate(): { publicKeyDer: string; fingerprint: string }
//     sign(args: { payloadBase64: string }): { signatureDer: string }
//
//   Android impl: KeyPairGenerator with KeyGenParameterSpec.Builder
//     .setKeySize(256)
//     .setDigests(KeyProperties.DIGEST_SHA256)
//     .setAlgorithmParameterSpec(ECGenParameterSpec("secp256r1"))
//     .setUserAuthenticationRequired(false)
//     // NOT export-flagged; private key never leaves Keystore.
//
//   iOS impl: SecKeyCreateRandomKey with kSecAttrKeyType=ECSECPrimeRandom,
//     kSecAttrKeySizeInBits=256, kSecAttrTokenID=kSecAttrTokenIDSecureEnclave.
//
// Until that plugin is built, native devices fall through to the web
// backend (still better than plaintext; not yet hardware-bound).

async function nativeBackend(): Promise<KeyHandle> {
  // const plugin = registerPlugin<SensarKeystorePlugin>('SensarKeystore');
  // const { publicKeyDer, fingerprint } = await plugin.getOrCreate();
  // return {
  //   alg: 'ECDSA_P256_SHA256',
  //   publicKeyDer,
  //   fingerprint,
  //   async sign(payload) {
  //     const b64 = btoa(String.fromCharCode(...payload));
  //     const { signatureDer } = await plugin.sign({ payloadBase64: b64 });
  //     return signatureDer;
  //   },
  // };
  return webBackend();
}

let cached: Promise<KeyHandle> | null = null;

/**
 * Returns the device signing key, using the Web Crypto backend.
 *
 * On Tizen OS (Samsung SSSP signage displays) the web backend is the
 * correct choice — Tizen's Chromium engine supports Web Crypto and
 * IndexedDB fully. The private key is stored with `extractable: false`
 * so it cannot be exported, which is the strongest protection available
 * in a pure-web context.
 *
 * If we ever add a native Capacitor wrapper (Android/iOS), swap in
 * `nativeBackend()` here for true hardware-backed key storage.
 */
export function getDeviceKey(): Promise<KeyHandle> {
  if (!cached) {
    cached = webBackend();
  }
  return cached;
}

/** Reset the cache — used after a hard re-pair / device reset. */
export function _resetForTest(): void {
  cached = null;
}
